import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Mail, Menu, X, Search, Upload, Clock, Shield } from 'lucide-react';
import './LandingPage.css';

const LandingPage = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  return (
    <div className="landing-page">
      {/* Navigation */}
      <nav className={`landing-nav ${isScrolled ? 'scrolled' : ''}`}>
        <div className="landing-nav-inner">
          <div className="landing-logo">
            Vendor<span>Scout</span>
          </div>
          <button className="mobile-menu-btn" onClick={toggleMobileMenu}>
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
          <ul className={`landing-nav-links ${isMobileMenuOpen ? 'mobile-open' : ''}`}>
            <li><a href="#features">Features</a></li>
            <li><a href="#how-it-works">How It Works</a></li>
            <li><a href="#for-whom">Who It's For</a></li>
            <li><a href="#contact">Contact</a></li>
            <li className="landing-nav-cta">
              <Link to="/login" className="btn btn-ghost">Login</Link>
            </li>
          </ul>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="landing-hero">
        <div className="hero-glow" />
        <div className="hero-glow-secondary" />
        <div className="hero-grid" />

        <div className="hero-content">
          <div className="hero-badge">
            <span className="hero-badge-dot" />
            Now in Beta
          </div>
          <h1 className="hero-title">
            Stop wasting time in sales calls just to get a price.
          </h1>
          <p className="hero-subtitle">
            VendorScout gives you real SaaS pricing data from IT pros who've already been through the process. No more demos just to see a number.
          </p>
          <div className="hero-cta-group">
            <Link to="/register" className="hero-cta-primary">
              Get Started Free
              <ArrowRight size={18} />
            </Link>
            <Link to="/login" className="hero-cta-secondary">
              Sign In
            </Link>
          </div>
        </div>
      </section>

      {/* App Preview Placeholder */}
      <section className="app-preview-section">
        <div className="app-preview-container">
          <div className="app-preview">
            <div className="app-preview-header">
              <div className="app-preview-dot" />
              <div className="app-preview-dot" />
              <div className="app-preview-dot" />
            </div>
            <div className="app-preview-content">
              <img
                src="/dashboard_preview.png"
                alt="VendorScout Dashboard Preview"
                className="app-preview-image"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="landing-features">
        <div className="section-header">
          <span className="section-tag">Features</span>
          <h2 className="section-title">Why VendorScout?</h2>
          <p className="section-subtitle">
            Get the pricing intel you need without the runaround.
          </p>
        </div>

        <div className="features-grid">
          <div className="feature-card">
            <div className="feature-icon">
              <Search size={24} />
            </div>
            <h3 className="feature-title">Real Pricing Data</h3>
            <p className="feature-description">
              Actual quotes from real deals, not marketing fluff or "contact us for pricing."
            </p>
          </div>

          <div className="feature-card">
            <div className="feature-icon">
              <Upload size={24} />
            </div>
            <h3 className="feature-title">Community Driven</h3>
            <p className="feature-description">
              IT admins and MSPs share what they're actually paying. You do the same.
            </p>
          </div>

          <div className="feature-card">
            <div className="feature-icon">
              <Clock size={24} />
            </div>
            <h3 className="feature-title">Skip the Runaround</h3>
            <p className="feature-description">
              No more scheduling demos just to find out if something fits your budget.
            </p>
          </div>

          <div className="feature-card">
            <div className="feature-icon">
              <Shield size={24} />
            </div>
            <h3 className="feature-title">Anonymous Uploads</h3>
            <p className="feature-description">
              Share quotes without your name attached. Your vendor relationships stay intact.
            </p>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="landing-how-it-works">
        <div className="section-header">
          <span className="section-tag">Process</span>
          <h2 className="section-title">How It Works</h2>
          <p className="section-subtitle">
            Four simple steps to pricing transparency.
          </p>
        </div>

        <div className="steps-container">
          <div className="step-card">
            <div className="step-number">1</div>
            <p className="step-text">Create a free account</p>
          </div>
          <div className="step-card">
            <div className="step-number">2</div>
            <p className="step-text">Search for the software you're evaluating</p>
          </div>
          <div className="step-card">
            <div className="step-number">3</div>
            <p className="step-text">See what others are paying</p>
          </div>
          <div className="step-card">
            <div className="step-number">4</div>
            <p className="step-text">Upload your quotes to help the community</p>
          </div>
        </div>

        <div className="info-grid">
          <div className="info-card">
            <h3 className="info-title">Your quotes stay anonymous</h3>
            <p className="info-text">
              When you upload a quote, we strip out identifying details before it goes into the database. Your vendor doesn't need to know you shared it.
            </p>
          </div>
          <div className="info-card">
            <h3 className="info-title">We verify everything</h3>
            <p className="info-text">
              Every quote gets reviewed before it's published. No fake data, no outdated info.
            </p>
          </div>
          <div className="info-card">
            <h3 className="info-title">It's free</h3>
            <p className="info-text">
              I built this because I was tired of wasting hours in meetings just to get basic pricing info. It's free to use.
            </p>
          </div>
        </div>
      </section>

      {/* Audience Section */}
      <section id="for-whom" className="landing-audience">
        <div className="section-header">
          <span className="section-tag">Audience</span>
          <h2 className="section-title">Built For</h2>
          <p className="section-subtitle">
            Whether you're buying for one company or many, we've got you covered.
          </p>
        </div>

        <div className="audience-grid">
          <div className="audience-card">
            <h3 className="audience-title">IT Managers</h3>
            <p className="audience-text">
              Get budget numbers before you commit to the sales process.
            </p>
          </div>
          <div className="audience-card">
            <h3 className="audience-title">MSPs</h3>
            <p className="audience-text">
              Know what margins look like across different vendors before you pitch to clients.
            </p>
          </div>
          <div className="audience-card">
            <h3 className="audience-title">Procurement Teams</h3>
            <p className="audience-text">
              Walk into negotiations knowing what others have paid.
            </p>
          </div>
          <div className="audience-card">
            <h3 className="audience-title">Startups</h3>
            <p className="audience-text">
              Get enterprise pricing intel without the enterprise sales cycle.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="landing-contact">
        <div className="section-header">
          <span className="section-tag">Contact</span>
          <h2 className="section-title">Questions?</h2>
        </div>
        <a href="mailto:hello@vendorscout.net" className="contact-card">
          <Mail size={20} />
          <span>hello@vendorscout.net</span>
        </a>
      </section>

      {/* Footer */}
      <footer className="landing-footer">
        <div className="footer-inner">
          <div className="footer-logo">VendorScout</div>
          <div className="footer-links">
            <a href="#privacy">Privacy</a>
            <a href="#terms">Terms</a>
          </div>
          <p className="footer-copyright">&copy; 2025 VendorScout</p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
